/**
 * EscrowAccountsPage — Admin Escrow Ledger Dashboard
 *
 * Calls:
 *   GET /credit/applications  → get all credit cases
 *   GET /escrow/ledger/:id/entries  → ledger entries per case
 *   GET /escrow/ledger/:id/balance  → balance per case
 *
 * Displays:
 *   - Summary cards (Total Credit, Total Debit, Net Balance)
 *   - Table of all ledger entries across accounts
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
} from '@mui/material';
import {
  AccountBalance,
  Search,
  FilterList,
  Download,
  ArrowUpward,
  ArrowDownward,
  Refresh,
} from '@mui/icons-material';
import { creditAPI, escrowAPI } from 'services/api';

// ─── Helpers ────────────────────────────────────────────────
const formatCurrency = (amount) => {
  if (amount == null) return '₹0';
  const n = parseFloat(amount);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const formatDate = (dt) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// ─── Entry Type Badge ────────────────────────────────────────
const EntryTypeBadge = ({ entryType }) => {
  const inflowTypes = ['CREDIT_IN', 'SETTLEMENT_IN', 'PREMIUM_IN'];
  const isInflow = inflowTypes.includes((entryType || '').toUpperCase());
  return (
    <div className="flex items-center gap-1">
      {isInflow ? (
        <ArrowUpward sx={{ fontSize: 14, color: '#10b981' }} />
      ) : (
        <ArrowDownward sx={{ fontSize: 14, color: '#ef4444' }} />
      )}
      <Chip
        label={(entryType || 'UNKNOWN').replace(/_/g, ' ')}
        size="small"
        variant="outlined"
        color={isInflow ? 'success' : 'error'}
        sx={{ fontWeight: 600, fontSize: '0.6rem', height: 22 }}
      />
    </div>
  );
};

// ═════════════════════════════════════════════════════════════
const EscrowAccountsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Data
  const [entries, setEntries] = useState([]);
  const [balances, setBalances] = useState([]); // per-account balances
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');

  const TYPES = ['ALL', 'CREDIT_IN', 'VENDOR_PAYMENT', 'SETTLEMENT_IN', 'FARMER_PAYOUT', 'LOAN_PRINCIPAL', 'LOAN_INTEREST', 'INSURANCE_PREMIUM', 'PREMIUM_IN'];

  // ── Load Data ─────────────────────────────────────────────
  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      // Step 1: Get all credit applications to know account IDs
      const appsRes = await creditAPI.getApplications();
      const apps = appsRes.data || [];

      // Step 2: Fetch ledger entries + balances for all active/disbursed/settled cases
      const relevantApps = apps.filter((a) => {
        const s = (a.status || '').toLowerCase();
        return ['disbursed', 'active', 'settled', 'approved', 'defaulted'].includes(s);
      });

      const entryPromises = relevantApps.map((a) =>
        escrowAPI.getLedgerEntries(a.id).then(
          (res) => (res.data || []).map((e) => ({ ...e, credit_case_id: e.account_id || a.id })),
          () => []
        )
      );
      const balancePromises = relevantApps.map((a) =>
        escrowAPI.getLedgerBalance(a.id).then(
          (res) => ({
            credit_case_id: a.id,
            farmer_name: a.farmer_name || a.user_name || `Farmer #${a.user_id || a.farmer_id || '—'}`,
            status: a.status,
            ...res.data,
          }),
          () => ({
            credit_case_id: a.id,
            farmer_name: a.farmer_name || a.user_name || `Farmer #${a.user_id || a.farmer_id || '—'}`,
            status: a.status,
            current_balance: 0,
            total_debits: 0,
            total_credits: 0,
            total_entries: 0,
          })
        )
      );

      const [entryResults, balanceResults] = await Promise.all([
        Promise.all(entryPromises),
        Promise.all(balancePromises),
      ]);

      // Flatten all entries and sort by date descending
      const allEntries = entryResults.flat().sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );

      setEntries(allEntries);
      setBalances(balanceResults.filter((b) => b.total_entries > 0 || parseFloat(b.current_balance) !== 0));
    } catch (err) {
      console.error('EscrowAccountsPage load error:', err);
      setError('Failed to load escrow ledger data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Derived ────────────────────────────────────────────────
  const inflowTypes = ['CREDIT_IN', 'SETTLEMENT_IN', 'PREMIUM_IN'];

  const totalInflow = useMemo(
    () =>
      entries
        .filter((e) => inflowTypes.includes((e.entry_type || '').toUpperCase()))
        .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0),
    [entries]
  );

  const totalOutflow = useMemo(
    () =>
      entries
        .filter((e) => !inflowTypes.includes((e.entry_type || '').toUpperCase()))
        .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0),
    [entries]
  );

  const netBalance = useMemo(
    () => balances.reduce((s, b) => s + (parseFloat(b.current_balance) || 0), 0),
    [balances]
  );

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      const matchSearch =
        !search ||
        (e.reference_id || '').toLowerCase().includes(search.toLowerCase()) ||
        (e.credit_case_id?.toString() || '').includes(search) ||
        (e.account_id?.toString() || '').includes(search);
      const matchType =
        typeFilter === 'ALL' || (e.entry_type || '').toUpperCase() === typeFilter;
      return matchSearch && matchType;
    });
  }, [entries, search, typeFilter]);

  // ── Export CSV ──────────────────────────────────────────────
  const exportCSV = () => {
    const header = 'Case ID,Type,Debit Account,Credit Account,Amount,Reference,Date\n';
    const rows = filtered
      .map(
        (e) =>
          `${e.credit_case_id || e.account_id},${e.entry_type},${e.debit_account},${e.credit_account},${parseFloat(e.amount) || 0},${e.reference_id || ''},${formatDate(e.created_at)}`
      )
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `escrow_ledger_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Loading ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <AccountBalance sx={{ fontSize: 22, color: '#059669' }} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Escrow Ledger</h1>
        </div>
        <div className="flex items-center justify-center py-20">
          <CircularProgress size={32} sx={{ color: '#059669' }} />
          <span className="ml-3 text-sm text-gray-500">Loading escrow data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <AccountBalance sx={{ fontSize: 22, color: '#059669' }} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Escrow Ledger</h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            Double-entry ledger — single source of truth for all escrow transactions
          </p>
        </div>
        <Tooltip title="Refresh data">
          <IconButton
            onClick={() => loadData(true)}
            disabled={refreshing}
            sx={{ backgroundColor: '#f1f5f9', '&:hover': { backgroundColor: '#e2e8f0' } }}
          >
            <Refresh
              sx={{
                fontSize: 20,
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
              }}
            />
          </IconButton>
        </Tooltip>
      </div>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} action={<Button size="small" onClick={() => loadData(true)}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {refreshing && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-5">
          <p className="text-xs text-emerald-600 font-medium mb-1">Total Inflow</p>
          <p className="text-2xl font-bold text-emerald-800">{formatCurrency(totalInflow)}</p>
          <p className="text-xs text-emerald-500 mt-1">
            {entries.filter((e) => inflowTypes.includes((e.entry_type || '').toUpperCase())).length} transactions
          </p>
        </div>
        <div className="rounded-2xl bg-red-50 border border-red-100 p-5">
          <p className="text-xs text-red-600 font-medium mb-1">Total Outflow</p>
          <p className="text-2xl font-bold text-red-800">{formatCurrency(totalOutflow)}</p>
          <p className="text-xs text-red-500 mt-1">
            {entries.filter((e) => !inflowTypes.includes((e.entry_type || '').toUpperCase())).length} transactions
          </p>
        </div>
        <div className="rounded-2xl bg-indigo-50 border border-indigo-100 p-5">
          <p className="text-xs text-indigo-600 font-medium mb-1">Net Balance</p>
          <p className="text-2xl font-bold text-indigo-800">{formatCurrency(netBalance)}</p>
          <p className="text-xs text-indigo-500 mt-1">
            Across {balances.length} account{balances.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* ── Account Balances Table ── */}
      {balances.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Account Balances</h3>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Case ID</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Farmer</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Debits</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Credits</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Balance</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Entries</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {balances.map((b) => (
                  <tr key={b.credit_case_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">#{b.credit_case_id}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 font-medium">{b.farmer_name}</td>
                    <td className="px-4 py-3">
                      <Chip
                        label={(b.status || 'unknown').toUpperCase()}
                        size="small"
                        variant="outlined"
                        color={
                          ['disbursed', 'active'].includes((b.status || '').toLowerCase())
                            ? 'success'
                            : (b.status || '').toLowerCase() === 'settled'
                              ? 'default'
                              : 'warning'
                        }
                        sx={{ fontWeight: 600, fontSize: '0.6rem', height: 22 }}
                      />
                    </td>
                    <td className="px-4 py-3 text-emerald-700 font-medium">{formatCurrency(b.total_debits)}</td>
                    <td className="px-4 py-3 text-red-600 font-medium">{formatCurrency(b.total_credits)}</td>
                    <td className="px-4 py-3 font-bold text-gray-900">{formatCurrency(b.current_balance)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{b.total_entries}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Transactions ── */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">All Ledger Entries</h3>

          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <TextField
              placeholder="Search by case ID or reference..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: '#9ca3af' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />

            <div className="flex items-center gap-1 flex-wrap">
              <FilterList sx={{ fontSize: 16, color: '#9ca3af', mr: 0.5 }} />
              {TYPES.slice(0, 6).map((t) => (
                <Chip
                  key={t}
                  label={t === 'ALL' ? 'All' : t.replace(/_/g, ' ')}
                  variant={typeFilter === t ? 'filled' : 'outlined'}
                  color={typeFilter === t ? 'primary' : 'default'}
                  size="small"
                  onClick={() => setTypeFilter(t)}
                  sx={{ fontWeight: 500, fontSize: '0.6rem', cursor: 'pointer' }}
                />
              ))}
            </div>

            <div className="ml-auto">
              <Tooltip title="Export CSV">
                <IconButton size="small" onClick={exportCSV}>
                  <Download sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-3">
            Showing {filtered.length} of {entries.length} entries
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Case ID</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Debit</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Credit</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Reference</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-gray-400 text-sm">
                    {entries.length === 0
                      ? 'No escrow ledger entries found. Entries appear after credit disbursement.'
                      : 'No entries match your filters'}
                  </td>
                </tr>
              ) : (
                filtered.slice(0, 200).map((entry, idx) => (
                  <tr key={entry.id || idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">
                      #{entry.credit_case_id || entry.account_id}
                    </td>
                    <td className="px-4 py-3">
                      <EntryTypeBadge entryType={entry.entry_type} />
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{entry.debit_account || '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{entry.credit_account || '—'}</td>
                    <td className="px-4 py-3 font-semibold text-gray-800">
                      {formatCurrency(parseFloat(entry.amount) || 0)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                      {entry.reference_id || '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {formatDate(entry.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {filtered.length > 200 && (
            <div className="px-4 py-3 text-xs text-gray-400 text-center border-t border-gray-100">
              Showing first 200 of {filtered.length} entries. Use filters or search to narrow results.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EscrowAccountsPage;
